import React from 'react';
import { useMsal } from '@azure/msal-react';
import { Box, Button, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { Field, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

// Esquema de validación Yup
const BridgeSchema = Yup.object().shape({
  bridgeIv: Yup.string().trim().max(32, 'Máximo 32 caracteres'),
  bridge_key: Yup.string().trim().max(64, 'Máximo 64 caracteres'),
  license_id: Yup.string().trim().max(36, 'Máximo 36 caracteres'),
  macAddress: Yup.string().trim().max(12, 'Máximo 12 caracteres'),
  name: Yup.string().trim().max(100, 'Máximo 100 caracteres'),
  serial_number: Yup.string().trim().required('El número de serie es obligatorio').max(40, 'Máximo 40 caracteres')
});

const CreateBridge = () => {
  const initialValues = {
    bridgeIv: '',
    bridge_key: '',
    license_id: '',
    macAddress: '',
    name: '',
    serial_number: ''
  };

  const { accounts } = useMsal();
  const { t } = useTranslation('admin', { keyPrefix: 'inventoryManagement.bridges' });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const API_URL = `${process.env.REACT_APP_API_URL}/sales/bridge`;

      // Enviar datos al API
      await axios.post(API_URL, {
        ...values,
        created_by: accounts?.[0]?.idTokenClaims?.sub
      });

      // Mostrar mensaje de éxito
      toast.success(t('success'));

      // Reiniciar el formulario
      resetForm();
    } catch (error) {
      console.error('Error al crear el bridge:', error);
      toast.error(t('error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        boxShadow: 3,
        margin: 'auto',
        maxWidth: 500,
        padding: 3
      }}
    >
      <Typography gutterBottom variant="h5">
        {t('createBridge')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={BridgeSchema}>
        {({ errors, touched, isSubmitting, isValid, dirty }) => (
          <Form>
            {/* Serial Number */}
            <Box mb={2}>
              <Field
                as={TextField}
                error={touched.serial_number && !!errors.serial_number}
                fullWidth
                helperText={touched.serial_number && errors.serial_number}
                label={t('serialNumber')}
                name="serial_number"
                variant="outlined"
              />
            </Box>

            {/* Mac Address */}
            <Box mb={2}>
              <Field
                as={TextField}
                error={touched.macAddress && !!errors.macAddress}
                fullWidth
                helperText={touched.macAddress && errors.macAddress}
                label={t('macAddress')}
                name="macAddress"
                variant="outlined"
              />
            </Box>

            {/* Bridge Key */}
            <Box mb={2}>
              <Field
                as={TextField}
                error={touched.bridge_key && !!errors.bridge_key}
                fullWidth
                helperText={touched.bridge_key && errors.bridge_key}
                label={t('bridgeKey')}
                name="bridge_key"
                variant="outlined"
              />
            </Box>

            {/* Bridge IV */}
            <Box mb={2}>
              <Field
                as={TextField}
                error={touched.bridgeIv && !!errors.bridgeIv}
                fullWidth
                helperText={touched.bridgeIv && errors.bridgeIv}
                label={t('bridgeIv')}
                name="bridgeIv"
                variant="outlined"
              />
            </Box>

            {/* License ID */}
            <Box mb={2}>
              <Field
                as={TextField}
                error={touched.license_id && !!errors.license_id}
                fullWidth
                helperText={touched.license_id && errors.license_id}
                label={t('licenseId')}
                name="license_id"
                variant="outlined"
              />
            </Box>

            {/* Name */}
            <Box mb={2}>
              <Field
                as={TextField}
                error={touched.name && !!errors.name}
                fullWidth
                helperText={touched.name && errors.name}
                label={t('name')}
                name="name"
                variant="outlined"
              />
            </Box>

            {/* Submit Button */}
            <Button color="primary" disabled={isSubmitting || !(isValid && dirty)} fullWidth type="submit" variant="contained">
              {isSubmitting ? t('creating') : t('createBridge')}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateBridge;
